import { useEffect, useRef, useState, useCallback } from "react";
import type { Socket } from "socket.io-client";
import { getLiveSocket } from "../../lib/socket.js";
import type { PlayerInfo, SessionSnapshot } from "./types.js";

export interface HostSessionState {
  phase: string;
  players: PlayerInfo[];
  pin: string;
  isConnected: boolean;
  snapshot: SessionSnapshot | null;
  lockLobby: (locked: boolean) => void;
  kickPlayer: (playerId: string) => void;
  updateSettings: (settings: {
    showLeaderboard?: boolean;
    randomizeOrder?: boolean;
    randomizeAnswers?: boolean;
    requireTwoStepJoin?: boolean;
  }) => void;
  startGame: () => void;
}

function configureSocketAuth(socket: Socket, auth: Record<string, string>): void {
  if (socket.connected && JSON.stringify(socket.auth ?? {}) !== JSON.stringify(auth)) {
    socket.disconnect();
  }
  socket.auth = auth;
}

export function useHostSession(sessionId: string): HostSessionState {
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [snapshot, setSnapshot] = useState<SessionSnapshot | null>(null);

  useEffect(() => {
    const socket = getLiveSocket();
    socketRef.current = socket;

    // No token in the handshake: the session cookie is httpOnly, so script
    // cannot read it. The browser attaches it to the WebSocket upgrade and the
    // server reads it from there.
    configureSocketAuth(socket, { type: "host" });

    function onConnect() {
      setIsConnected(true);
      socket.emit("host:join", { sessionId });
    }
    function onDisconnect() {
      setIsConnected(false);
    }
    function onSnapshot(data: SessionSnapshot) {
      setSnapshot(data);
    }
    function onStateChanged(data: { phase: string }) {
      setSnapshot((prev) => (prev ? { ...prev, phase: data.phase } : prev));
    }
    function onPlayersChanged(data: { players: PlayerInfo[] }) {
      setSnapshot((prev) =>
        prev ? { ...prev, host: { ...(prev.host ?? {}), players: data.players } } : prev
      );
    }

    socket.on("connect", onConnect);
    socket.on("disconnect", onDisconnect);
    socket.on("session:snapshot", onSnapshot);
    socket.on("session:stateChanged", onStateChanged);
    socket.on("lobby:playersChanged", onPlayersChanged);

    if (socket.connected) onConnect();
    else socket.connect();

    return () => {
      socket.off("connect", onConnect);
      socket.off("disconnect", onDisconnect);
      socket.off("session:snapshot", onSnapshot);
      socket.off("session:stateChanged", onStateChanged);
      socket.off("lobby:playersChanged", onPlayersChanged);
    };
  }, [sessionId]);

  const lockLobby = useCallback((locked: boolean) => {
    socketRef.current?.emit("host:lockLobby", { locked });
  }, []);

  const kickPlayer = useCallback((playerId: string) => {
    socketRef.current?.emit("host:kickPlayer", { playerId });
  }, []);

  const updateSettings = useCallback((settings: {
    showLeaderboard?: boolean;
    randomizeOrder?: boolean;
    randomizeAnswers?: boolean;
    requireTwoStepJoin?: boolean;
  }) => {
    socketRef.current?.emit("host:updateSettings", settings);
  }, []);

  const startGame = useCallback(() => {
    socketRef.current?.emit("host:startGame", { sessionId });
  }, [sessionId]);

  return {
    phase: snapshot?.phase ?? "open_lobby",
    players: snapshot?.host?.players ?? [],
    pin: snapshot?.pin ?? "",
    isConnected,
    snapshot,
    lockLobby,
    kickPlayer,
    updateSettings,
    startGame,
  };
}
